import { Check, Close, Edit } from "@mui/icons-material";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  TextField,
  TextareaAutosize,
} from "@mui/material";
import { TextareaStyle } from "theme";
import { isNotUndefined, parseSafeJSON } from "Backend";
import { ProfileGenTestCaseView } from "generated/graphql";
import { useEffect, useState } from "react";
import { TestCaseLabels } from "redux/slices/lensEditor";
import useTestCaseManager from "./useTestCaseManager";

interface DescriptionPOJO {
  labels: TestCaseLabels[];
  text?: string;
}

export function EditableTestCaseDescription({ testCase }: { testCase: ProfileGenTestCaseView }) {
  const { description } = testCase;
  const parsedDescription = parseSafeJSON<DescriptionPOJO>(description);
  const { updateTestCase } = useTestCaseManager();
  const [descriptionEditMode, setDescriptionEditMode] = useState<boolean>(false);
  const [text, setText] = useState(parsedDescription?.text ?? "");

  const handleUpdateDescription = async () => {
    updateTestCase({
      ...testCase,
      description: JSON.stringify({ labels: parsedDescription?.labels, text }),
    });
    setDescriptionEditMode(false);
  };

  return descriptionEditMode ? (
    <div className="flex justify-between items-center grow">
      <TextareaAutosize
        maxRows={4}
        minRows={4}
        value={text}
        style={{
          ...TextareaStyle,
          height: "18px",
          fontSize: "18px",
          flexGrow: 1,
        }}
        onChange={(evt) => setText(evt.target.value)}
        onKeyDown={(evt) => {
          if (evt.key === "Enter") {
            evt.preventDefault();
            handleUpdateDescription();
          }
        }}
      />
      <IconButton onClick={handleUpdateDescription}>
        <Check />
      </IconButton>
      <IconButton
        onClick={() => {
          setDescriptionEditMode(false);
        }}>
        <Close />
      </IconButton>
    </div>
  ) : (
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-wrap">{parsedDescription?.text || "<missing description>"}</p>
      <IconButton
        onClick={() => {
          setDescriptionEditMode(true);
        }}>
        <Edit />
      </IconButton>
    </div>
  );
}

export function TestCaseLabelsAndDescription({
  onChange,
  description,
}: {
  onChange: (description: string) => void;
  description: string;
}) {
  const parsedDescription = parseSafeJSON<DescriptionPOJO>(description);
  const [text, setText] = useState(parsedDescription?.text ?? "");
  const [starred, setStarred] = useState(!!parsedDescription?.labels.find((l) => l === TestCaseLabels.Starred));
  const [failing, setFailing] = useState(!!parsedDescription?.labels.find((l) => l === TestCaseLabels.Failing));
  const [notOnPage, setNotOnPage] = useState(!!parsedDescription?.labels.find((l) => l === TestCaseLabels.NotOnPage));
  const [badFinding, setBadFinding] = useState(
    !!parsedDescription?.labels.find((l) => l === TestCaseLabels.BadFinding)
  );
  const pojoDescription = {
    labels: [
      starred ? TestCaseLabels.Starred : undefined,
      failing ? TestCaseLabels.Failing : undefined,
      notOnPage ? TestCaseLabels.NotOnPage : undefined,
      badFinding ? TestCaseLabels.BadFinding : undefined,
    ].filter(isNotUndefined),
    text,
  };

  useEffect(() => {
    onChange(JSON.stringify(pojoDescription));
  }, [text, starred, failing, notOnPage, badFinding]);

  return (
    <div className="flex flex-col gap-2">
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">Assign Labels</FormLabel>
        <FormGroup row>
          <FormControlLabel
            label="Starred"
            control={<Checkbox checked={starred} onChange={() => setStarred((prev) => !prev)} name="Starred" />}
          />
          <FormControlLabel
            label="Failing"
            control={<Checkbox checked={failing} onChange={() => setFailing((prev) => !prev)} name="Failing" />}
          />
          <FormControlLabel
            label="Not on Page"
            control={<Checkbox checked={notOnPage} onChange={() => setNotOnPage((prev) => !prev)} name="NotOnPage" />}
          />
          <FormControlLabel
            label="Bad Finding"
            control={
              <Checkbox checked={badFinding} onChange={() => setBadFinding((prev) => !prev)} name="BadFinding" />
            }
          />
        </FormGroup>
      </FormControl>
      <FormControl>
        <TextField label="Description" value={text} onChange={(evt) => setText(evt.target.value)} />
      </FormControl>
    </div>
  );
}
